import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Chip,
    Divider,
    Grid
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    AttachMoney as MoneyIcon,
    CreditCard as CreditCardIcon,
    AccountBalance as BankIcon,
    Money as CashIcon
} from '@mui/icons-material';
import * as http from '../../common/http';
import { APIUrl } from '../../common/apiConfig';

interface FeeCollectionData {
    total_collected: number;
    payment_count: number;
    by_mode: {
        [key: string]: {
            amount: number;
            count: number;
        };
    };
    pending_amount: number;
    date: string;
}

const FeeCollectionToday: React.FC<{ branchId?: number }> = ({ branchId }) => {
    const [data, setData] = useState<FeeCollectionData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            let url = `${APIUrl}dashboard-coaching/fee-collection-today`;
            if (branchId) {
                url += `?branch_id=${branchId}`;
            }
            
            const response = await http.get(url);
            if (response && response.status === 'success') {
                setData(response.result);
                setLastRefresh(new Date());
            } else {
                setError('Failed to fetch fee collection data');
            }
        } catch (err) {
            console.error('Error fetching fee collection:', err);
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        
        // Auto-refresh every 5 minutes
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        
        return () => clearInterval(interval);
    }, [branchId]);

    const getPaymentModeIcon = (mode: string) => {
        switch (mode.toLowerCase()) {
            case 'cash':
                return <CashIcon />;
            case 'online':
            case 'upi':
                return <CreditCardIcon />;
            case 'cheque':
                return <BankIcon />;
            default:
                return <MoneyIcon />;
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    if (loading && !data) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Alert severity="error">{error}</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={2}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                        Today's Fee Collection
                    </Typography>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={fetchData} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Total Collected */}
                <Box 
                    p={3} 
                    mb={2} 
                    sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 2,
                        color: 'white'
                    }}
                >
                    <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Total Collected Today
                    </Typography>
                    <Typography variant="h3" fontWeight="bold">
                        {formatCurrency(data?.total_collected || 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {data?.payment_count || 0} payments received
                    </Typography>
                </Box>

                {/* Payment Mode Breakdown */}
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Payment Mode Breakdown
                </Typography>
                <Grid container spacing={2} mb={2}>
                    {data?.by_mode && Object.keys(data.by_mode).length > 0 ? (
                        Object.entries(data.by_mode).map(([mode, details]) => (
                            <Grid size={{ xs: 6 }} key={mode}>
                                <Box 
                                    p={2} 
                                    sx={{ 
                                        border: '1px solid #e0e0e0', 
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <Box sx={{ color: '#1976d2' }}>
                                        {getPaymentModeIcon(mode)}
                                    </Box>
                                    <Box flex={1}>
                                        <Typography variant="caption" color="text.secondary">
                                            {mode.toUpperCase()}
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {formatCurrency(details.amount)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {details.count} transactions
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))
                    ) : (
                        <Grid size={{ xs: 12 }}>
                            <Alert severity="info">No payments received today</Alert>
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Pending Fees */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        Total Pending Fees
                    </Typography>
                    <Chip 
                        label={formatCurrency(data?.pending_amount || 0)}
                        color="warning"
                        sx={{ fontWeight: 'bold' }}
                    />
                </Box>

                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    Last updated: {lastRefresh.toLocaleTimeString()}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default FeeCollectionToday;
